import React, { useRef, useEffect } from 'react';
import { setupCanvas } from './canvasUtils';

// ============================================
// 1. EM Spectrum  -  Ch 3: Speed of Light
// Interactive wavelength/frequency/energy scale
// ============================================
export const EMSpectrum = () => {
    const canvasRef = useRef(null);

    useEffect(() => {
        const W = 560, H = 160;
        const ctx = setupCanvas(canvasRef.current, W, H);

        ctx.fillStyle = '#FAFAFA';
        ctx.fillRect(0, 0, W, H);

        // Title
        ctx.fillStyle = '#1E293B';
        ctx.font = 'bold 15px Inter, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('Electromagnetic Spectrum', W / 2, 24);

        const bands = [
            { name: 'Radio',      wl: '>1 m',       f: '<300 MHz',    E: '<1.24 \u00B5eV', color: '#ef4444', x: 0 },
            { name: 'Microwave',  wl: '1 mm\u20131 m', f: '300 MHz\u2013300 GHz', E: '\u00B5eV\u2013meV', color: '#f97316', x: 1 },
            { name: 'Infrared',   wl: '700 nm\u20131 mm', f: '300 GHz\u2013430 THz', E: 'meV\u20131.7 eV', color: '#f59e0b', x: 2 },
            { name: 'Visible',    wl: '400\u2013700 nm', f: '430\u2013750 THz', E: '1.7\u20133.1 eV', color: '#22c55e', x: 3 },
            { name: 'Ultraviolet',wl: '10\u2013400 nm', f: '750 THz\u201330 PHz', E: '3.1\u2013124 eV', color: '#3b82f6', x: 4 },
            { name: 'X-ray',      wl: '0.01\u201310 nm', f: '30 PHz\u201330 EHz', E: '124 eV\u2013124 keV', color: '#8b5cf6', x: 5 },
            { name: 'Gamma',      wl: '<0.01 nm',    f: '>30 EHz',     E: '>124 keV', color: '#ec4899', x: 6 },
        ];

        const barY = 50, barH = 40;
        const bandW = (W - 20) / bands.length;

        bands.forEach((b, i) => {
            const x = 10 + i * bandW;
            // Bar
            ctx.fillStyle = b.color;
            ctx.beginPath();
            ctx.roundRect(x + 1, barY, bandW - 2, barH, 4);
            ctx.fill();
            // Name
            ctx.fillStyle = 'white';
            ctx.font = 'bold 10px Inter, sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText(b.name, x + bandW / 2, barY + 16);
            // Wavelength
            ctx.fillStyle = '#475569';
            ctx.font = '9px Inter, sans-serif';
            ctx.fillText('\u03BB: ' + b.wl, x + bandW / 2, barY + barH + 16);
            // Frequency
            ctx.fillStyle = '#64748B';
            ctx.fillText('f: ' + b.f, x + bandW / 2, barY + barH + 30);
            // Energy
            ctx.fillStyle = '#94A3B8';
            ctx.fillText('E: ' + b.E, x + bandW / 2, barY + barH + 44);
        });

        // Visible light detail - rainbow gradient
        const visX = 10 + 3 * bandW + 2;
        const rainbowGrad = ctx.createLinearGradient(visX, 0, visX + bandW - 4, 0);
        rainbowGrad.addColorStop(0, '#8B00FF');
        rainbowGrad.addColorStop(0.17, '#4B0082');
        rainbowGrad.addColorStop(0.33, '#0000FF');
        rainbowGrad.addColorStop(0.5, '#00FF00');
        rainbowGrad.addColorStop(0.67, '#FFFF00');
        rainbowGrad.addColorStop(0.83, '#FF7F00');
        rainbowGrad.addColorStop(1, '#FF0000');
        ctx.fillStyle = rainbowGrad;
        ctx.fillRect(visX, barY + barH - 6, bandW - 4, 6);

        // Arrows
        ctx.fillStyle = '#64748B';
        ctx.font = '10px Inter, sans-serif';
        ctx.textAlign = 'left';
        ctx.fillText('\u2190 Longer wavelength', 15, H - 12);
        ctx.textAlign = 'right';
        ctx.fillText('Higher energy \u2192', W - 15, H - 12);

        // c = lambda * f
        ctx.textAlign = 'center';
        ctx.fillStyle = '#1E293B';
        ctx.font = 'bold 11px Inter, sans-serif';
        ctx.fillText('c = \u03BB \u00D7 f = 299,792,458 m/s', W / 2, H - 26);

    }, []);

    return (
        <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-indigo-50 rounded-2xl border border-slate-200">
            <canvas ref={canvasRef} width={560} height={160} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};


// ============================================
// 2. Time Dilation Diagram  -  Ch 4/11
// Animated twin clocks showing gamma factor
// ============================================
export const TimeDilationDiagram = () => {
    const canvasRef = useRef(null);

    useEffect(() => {
        const W = 510, H = 220;
        const ctx = setupCanvas(canvasRef.current, W, H);
        let frame = 0;
        let animId;

        const drawClock = (cx, cy, r, angle, label, color) => {
            // Face
            ctx.beginPath();
            ctx.arc(cx, cy, r, 0, Math.PI * 2);
            ctx.fillStyle = 'white';
            ctx.fill();
            ctx.strokeStyle = color;
            ctx.lineWidth = 3;
            ctx.stroke();

            // Hour marks
            for (let i = 0; i < 12; i++) {
                const a = (i / 12) * Math.PI * 2 - Math.PI / 2;
                const inner = r - 8, outer = r - 3;
                ctx.beginPath();
                ctx.moveTo(cx + Math.cos(a) * inner, cy + Math.sin(a) * inner);
                ctx.lineTo(cx + Math.cos(a) * outer, cy + Math.sin(a) * outer);
                ctx.strokeStyle = '#64748B';
                ctx.lineWidth = 2;
                ctx.stroke();
            }

            // Hand
            const handLen = r - 15;
            ctx.beginPath();
            ctx.moveTo(cx, cy);
            ctx.lineTo(cx + Math.cos(angle - Math.PI / 2) * handLen, cy + Math.sin(angle - Math.PI / 2) * handLen);
            ctx.strokeStyle = color;
            ctx.lineWidth = 3;
            ctx.lineCap = 'round';
            ctx.stroke();

            // Center dot
            ctx.beginPath();
            ctx.arc(cx, cy, 4, 0, Math.PI * 2);
            ctx.fillStyle = color;
            ctx.fill();

            // Label
            ctx.fillStyle = '#1E293B';
            ctx.font = 'bold 12px Inter, sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText(label, cx, cy + r + 22);
        };

        const draw = () => {
            ctx.clearRect(0, 0, W, H);
            ctx.fillStyle = '#FAFAFA';
            ctx.fillRect(0, 0, W, H);

            // Title
            ctx.fillStyle = '#1E293B';
            ctx.font = 'bold 14px Inter, sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText('Time Dilation: Moving Clocks Run Slower', W / 2, 22);

            const speed = 0.02;
            const beta = 0.87; // v = 0.87c
            const gamma = 1 / Math.sqrt(1 - beta * beta);

            // Stationary clock (runs normally)
            const stationaryAngle = frame * speed;
            drawClock(130, 110, 55, stationaryAngle, 'Stationary Clock', '#3B82F6');

            // Moving clock (runs slower by gamma)
            const movingAngle = frame * speed / gamma;
            drawClock(380, 110, 55, movingAngle, 'Moving Clock (0.87c)', '#EF4444');

            // Time display
            const tStationary = (frame * speed / (Math.PI * 2)).toFixed(1);
            const tMoving = (frame * speed / gamma / (Math.PI * 2)).toFixed(1);

            ctx.font = '13px Inter, sans-serif';
            ctx.fillStyle = '#3B82F6';
            ctx.fillText('t = ' + tStationary + ' s', 130, 190);
            ctx.fillStyle = '#EF4444';
            ctx.fillText("t' = " + tMoving + ' s', 380, 190);

            // Formula
            ctx.fillStyle = '#475569';
            ctx.font = '12px Inter, sans-serif';
            ctx.fillText('\u0394t\' = \u0394t / \u03B3     \u03B3 = ' + gamma.toFixed(2) + '     v = 0.87c', W / 2, H - 14);

            // Arrow between clocks
            ctx.strokeStyle = '#CBD5E1';
            ctx.lineWidth = 1;
            ctx.setLineDash([4, 4]);
            ctx.beginPath();
            ctx.moveTo(200, 110);
            ctx.lineTo(310, 110);
            ctx.stroke();
            ctx.setLineDash([]);

            // Speed arrow on moving clock
            ctx.fillStyle = '#EF4444';
            ctx.font = 'bold 18px Inter, sans-serif';
            ctx.fillText('\u2192', 255, 106);

            frame++;
            animId = requestAnimationFrame(draw);
        };

        draw();
        return () => cancelAnimationFrame(animId);
    }, []);

    return (
        <div className="my-8 p-6 bg-gradient-to-br from-blue-50 to-red-50 rounded-2xl border border-slate-200">
            <canvas ref={canvasRef} width={510} height={220} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
            <p className="text-center text-sm text-slate-500 mt-3">Watch the moving clock tick slower due to relativistic time dilation</p>
        </div>
    );
};


// ============================================
// 3. Accelerator Types  -  Ch 5
// Linac, Cyclotron, Synchrotron comparison
// ============================================
export const AcceleratorTypes = () => {
    const canvasRef = useRef(null);

    useEffect(() => {
        const W = 520, H = 190;
        const ctx = setupCanvas(canvasRef.current, W, H);
        let frame = 0;
        let animId;

        const draw = () => {
            ctx.clearRect(0, 0, W, H);
            ctx.fillStyle = '#FAFAFA';
            ctx.fillRect(0, 0, W, H);

            ctx.fillStyle = '#1E293B';
            ctx.font = 'bold 14px Inter, sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText('Particle Accelerator Types', W / 2, 22);

            // --- 1. Linac (leftmost) ---
            const linacX = 25, linacY = 55;
            ctx.fillStyle = '#E0F2FE';
            ctx.beginPath();
            ctx.roundRect(linacX, linacY, 140, 70, 8);
            ctx.fill();
            ctx.strokeStyle = '#3B82F6';
            ctx.lineWidth = 2;
            ctx.stroke();

            // RF cavities
            for (let i = 0; i < 5; i++) {
                const cx = linacX + 20 + i * 25;
                ctx.fillStyle = '#93C5FD';
                ctx.fillRect(cx, linacY + 20, 12, 30);
                ctx.strokeStyle = '#3B82F6';
                ctx.lineWidth = 1;
                ctx.strokeRect(cx, linacY + 20, 12, 30);
            }

            // Particle moving through linac
            const linacParticleX = linacX + 10 + ((frame * 0.8) % 130);
            ctx.beginPath();
            ctx.arc(linacParticleX, linacY + 35, 4, 0, Math.PI * 2);
            ctx.fillStyle = '#EF4444';
            ctx.fill();

            ctx.fillStyle = '#1E293B';
            ctx.font = 'bold 11px Inter, sans-serif';
            ctx.fillText('Linear Accelerator', linacX + 70, linacY + 82);
            ctx.font = '9px Inter, sans-serif';
            ctx.fillStyle = '#64748B';
            ctx.fillText('Straight path, RF cavities', linacX + 70, linacY + 95);

            // --- 2. Cyclotron (center) ---
            const cycCx = W / 2, cycCy = 100;
            ctx.beginPath();
            ctx.arc(cycCx, cycCy, 42, 0, Math.PI * 2);
            ctx.fillStyle = '#FEF3C7';
            ctx.fill();
            ctx.strokeStyle = '#F59E0B';
            ctx.lineWidth = 2;
            ctx.stroke();

            // D-electrodes
            ctx.beginPath();
            ctx.arc(cycCx, cycCy, 38, -Math.PI / 2, Math.PI / 2);
            ctx.fillStyle = '#FDE68A';
            ctx.fill();
            ctx.beginPath();
            ctx.arc(cycCx, cycCy, 38, Math.PI / 2, -Math.PI / 2);
            ctx.fillStyle = '#FCD34D';
            ctx.fill();

            // Gap line
            ctx.strokeStyle = '#F59E0B';
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(cycCx, cycCy - 40);
            ctx.lineTo(cycCx, cycCy + 40);
            ctx.stroke();

            // Spiral particle
            const spiralAngle = frame * 0.04;
            const spiralR = 8 + (spiralAngle % (Math.PI * 6)) / (Math.PI * 6) * 30;
            const px = cycCx + Math.cos(spiralAngle) * spiralR;
            const py = cycCy + Math.sin(spiralAngle) * spiralR;
            ctx.beginPath();
            ctx.arc(px, py, 4, 0, Math.PI * 2);
            ctx.fillStyle = '#EF4444';
            ctx.fill();

            ctx.fillStyle = '#1E293B';
            ctx.font = 'bold 11px Inter, sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText('Cyclotron', cycCx, cycCy + 60);
            ctx.font = '9px Inter, sans-serif';
            ctx.fillStyle = '#64748B';
            ctx.fillText('Spiral path, fixed B field', cycCx, cycCy + 73);

            // --- 3. Synchrotron (right) ---
            const synCx = W - 95, synCy = 100;
            ctx.beginPath();
            ctx.arc(synCx, synCy, 42, 0, Math.PI * 2);
            ctx.strokeStyle = '#22C55E';
            ctx.lineWidth = 3;
            ctx.stroke();

            // Inner (vacuum pipe)
            ctx.beginPath();
            ctx.arc(synCx, synCy, 36, 0, Math.PI * 2);
            ctx.strokeStyle = '#BBF7D0';
            ctx.lineWidth = 8;
            ctx.stroke();

            // Dipole magnets
            for (let i = 0; i < 4; i++) {
                const a = (i / 4) * Math.PI * 2;
                const mx = synCx + Math.cos(a) * 42;
                const my = synCy + Math.sin(a) * 42;
                ctx.beginPath();
                ctx.arc(mx, my, 6, 0, Math.PI * 2);
                ctx.fillStyle = '#4ADE80';
                ctx.fill();
                ctx.strokeStyle = '#16A34A';
                ctx.lineWidth = 1;
                ctx.stroke();
            }

            // Circulating particle
            const synAngle = frame * 0.03;
            const synPx = synCx + Math.cos(synAngle) * 36;
            const synPy = synCy + Math.sin(synAngle) * 36;
            ctx.beginPath();
            ctx.arc(synPx, synPy, 4, 0, Math.PI * 2);
            ctx.fillStyle = '#EF4444';
            ctx.fill();

            // Trail
            for (let t = 1; t <= 8; t++) {
                const trailA = synAngle - t * 0.08;
                const tx = synCx + Math.cos(trailA) * 36;
                const ty = synCy + Math.sin(trailA) * 36;
                ctx.beginPath();
                ctx.arc(tx, ty, 2, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(239,68,68,${0.5 - t * 0.06})`;
                ctx.fill();
            }

            ctx.fillStyle = '#1E293B';
            ctx.font = 'bold 11px Inter, sans-serif';
            ctx.fillText('Synchrotron', synCx, synCy + 60);
            ctx.font = '9px Inter, sans-serif';
            ctx.fillStyle = '#64748B';
            ctx.fillText('Fixed ring, varying B field', synCx, synCy + 73);

            // Comparison note
            ctx.fillStyle = '#475569';
            ctx.font = '10px Inter, sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText('LHC: 27 km synchrotron ring | Up to 6.5 TeV per beam', W / 2, H - 10);

            frame++;
            animId = requestAnimationFrame(draw);
        };

        draw();
        return () => cancelAnimationFrame(animId);
    }, []);

    return (
        <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-emerald-50 rounded-2xl border border-slate-200">
            <canvas ref={canvasRef} width={520} height={190} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};


// ============================================
// 4. Detector Cross Section  -  Ch 6
// Layered detector with particle paths
// ============================================
export const DetectorCrossSection = () => {
    const canvasRef = useRef(null);

    useEffect(() => {
        const W = 480, H = 320;
        const ctx = setupCanvas(canvasRef.current, W, H);
        const cx = W / 2, cy = H / 2 + 10;

        ctx.fillStyle = '#FAFAFA';
        ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B';
        ctx.font = 'bold 14px Inter, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('Particle Detector Cross-Section', W / 2, 22);

        const layers = [
            { r: 120, color: '#DBEAFE', border: '#93C5FD', label: 'Muon Chambers' },
            { r: 95,  color: '#FEE2E2', border: '#FCA5A5', label: 'Hadronic Cal.' },
            { r: 72,  color: '#FEF3C7', border: '#FCD34D', label: 'EM Calorimeter' },
            { r: 50,  color: '#D1FAE5', border: '#6EE7B7', label: 'Tracker' },
            { r: 20,  color: '#F3E8FF', border: '#C4B5FD', label: 'Beam Pipe' },
        ];

        // Draw layers (outside in)
        layers.forEach(l => {
            ctx.beginPath();
            ctx.arc(cx, cy, l.r, 0, Math.PI * 2);
            ctx.fillStyle = l.color;
            ctx.fill();
            ctx.strokeStyle = l.border;
            ctx.lineWidth = 2;
            ctx.stroke();
        });

        // Labels
        const labelConfigs = [
            { angle: -0.3, r: 108, text: 'Muon Chambers', color: '#3B82F6' },
            { angle: 0.6, r: 84, text: 'HCAL', color: '#EF4444' },
            { angle: -1.2, r: 62, text: 'ECAL', color: '#F59E0B' },
            { angle: 1.8, r: 42, text: 'Tracker', color: '#22C55E' },
        ];

        labelConfigs.forEach(lc => {
            const lx = cx + Math.cos(lc.angle) * lc.r;
            const ly = cy + Math.sin(lc.angle) * lc.r;
            ctx.fillStyle = lc.color;
            ctx.font = 'bold 9px Inter, sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText(lc.text, lx, ly);
        });

        // Particle paths
        // Electron (stops in ECAL)
        ctx.strokeStyle = '#3B82F6';
        ctx.lineWidth = 2;
        ctx.setLineDash([3, 2]);
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.lineTo(cx - 35, cy - 55);
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(cx - 35, cy - 55, 6, 0, Math.PI * 2);
        ctx.fillStyle = '#DBEAFE';
        ctx.fill();
        ctx.strokeStyle = '#3B82F6';
        ctx.stroke();
        ctx.fillStyle = '#3B82F6';
        ctx.font = 'bold 8px Inter';
        ctx.fillText('e\u207B', cx - 35, cy - 53);

        // Muon (goes through everything)
        ctx.strokeStyle = '#8B5CF6';
        ctx.setLineDash([3, 2]);
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.lineTo(cx + 60, cy - 100);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.beginPath();
        ctx.arc(cx + 60, cy - 100, 6, 0, Math.PI * 2);
        ctx.fillStyle = '#F3E8FF';
        ctx.fill();
        ctx.strokeStyle = '#8B5CF6';
        ctx.stroke();
        ctx.fillStyle = '#8B5CF6';
        ctx.font = 'bold 8px Inter';
        ctx.fillText('\u03BC', cx + 60, cy - 98);

        // Hadron (stops in HCAL)
        ctx.strokeStyle = '#EF4444';
        ctx.setLineDash([3, 2]);
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.lineTo(cx + 20, cy + 80);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.beginPath();
        ctx.arc(cx + 20, cy + 80, 6, 0, Math.PI * 2);
        ctx.fillStyle = '#FEE2E2';
        ctx.fill();
        ctx.strokeStyle = '#EF4444';
        ctx.stroke();
        ctx.fillStyle = '#EF4444';
        ctx.font = 'bold 8px Inter';
        ctx.fillText('\u03C0', cx + 20, cy + 82);

        ctx.setLineDash([]);

        // Legend
        const legendY = H - 30;
        [
            { label: 'e\u207B (stops in ECAL)', color: '#3B82F6', x: W / 2 - 140 },
            { label: '\u03BC (passes through)', color: '#8B5CF6', x: W / 2 },
            { label: '\u03C0 (stops in HCAL)', color: '#EF4444', x: W / 2 + 140 },
        ].forEach(item => {
            ctx.beginPath();
            ctx.arc(item.x - 30, legendY, 4, 0, Math.PI * 2);
            ctx.fillStyle = item.color;
            ctx.fill();
            ctx.fillStyle = '#475569';
            ctx.font = '10px Inter, sans-serif';
            ctx.textAlign = 'left';
            ctx.fillText(item.label, item.x - 22, legendY + 3);
        });

    }, []);

    return (
        <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-purple-50 rounded-2xl border border-slate-200">
            <canvas ref={canvasRef} width={480} height={320} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};


// ============================================
// 5. Decay Curve  -  Ch 13
// Animated exponential decay N(t)=N0*e^(-t/tau)
// ============================================
export const DecayCurve = () => {
    const canvasRef = useRef(null);

    useEffect(() => {
        const W = 500, H = 300;
        const ctx = setupCanvas(canvasRef.current, W, H);
        let frame = 0;
        let animId;

        const draw = () => {
            ctx.clearRect(0, 0, W, H);
            ctx.fillStyle = '#FAFAFA';
            ctx.fillRect(0, 0, W, H);

            const margin = { top: 40, right: 30, bottom: 45, left: 55 };
            const plotW = W - margin.left - margin.right;
            const plotH = H - margin.top - margin.bottom;

            // Title
            ctx.fillStyle = '#1E293B';
            ctx.font = 'bold 14px Inter, sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText('Exponential Decay: N(t) = N\u2080 \u00B7 e^(\u2212t/\u03C4)', W / 2, 22);

            // Axes
            ctx.strokeStyle = '#1E293B';
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.moveTo(margin.left, margin.top);
            ctx.lineTo(margin.left, margin.top + plotH);
            ctx.lineTo(margin.left + plotW, margin.top + plotH);
            ctx.stroke();

            // Y-axis label
            ctx.save();
            ctx.translate(15, margin.top + plotH / 2);
            ctx.rotate(-Math.PI / 2);
            ctx.fillStyle = '#475569';
            ctx.font = '11px Inter, sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText('N(t) / N\u2080', 0, 0);
            ctx.restore();

            // X-axis label
            ctx.fillStyle = '#475569';
            ctx.font = '11px Inter, sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText('Time (t / \u03C4)', margin.left + plotW / 2, H - 8);

            // Grid lines and labels
            const maxT = 5; // 5 tau
            for (let i = 0; i <= maxT; i++) {
                const x = margin.left + (i / maxT) * plotW;
                ctx.strokeStyle = '#E2E8F0';
                ctx.lineWidth = 1;
                ctx.beginPath();
                ctx.moveTo(x, margin.top);
                ctx.lineTo(x, margin.top + plotH);
                ctx.stroke();
                ctx.fillStyle = '#64748B';
                ctx.font = '10px Inter, sans-serif';
                ctx.textAlign = 'center';
                ctx.fillText(i + '\u03C4', x, margin.top + plotH + 15);
            }

            // Y grid
            [1, 0.5, 0.25, 0].forEach(v => {
                const y = margin.top + (1 - v) * plotH;
                if (v > 0 && v < 1) {
                    ctx.strokeStyle = '#E2E8F0';
                    ctx.lineWidth = 1;
                    ctx.beginPath();
                    ctx.moveTo(margin.left, y);
                    ctx.lineTo(margin.left + plotW, y);
                    ctx.stroke();
                }
                ctx.fillStyle = '#64748B';
                ctx.font = '10px Inter, sans-serif';
                ctx.textAlign = 'right';
                ctx.fillText(v.toFixed(2), margin.left - 8, y + 3);
            });

            // Animated decay curve
            const progress = Math.min(frame / 120, 1); // animate over 120 frames
            const tMax = progress * maxT;

            ctx.beginPath();
            ctx.strokeStyle = '#3B82F6';
            ctx.lineWidth = 3;
            for (let px = 0; px <= plotW * progress; px++) {
                const t = (px / plotW) * maxT;
                const n = Math.exp(-t);
                const x = margin.left + px;
                const y = margin.top + (1 - n) * plotH;
                if (px === 0) ctx.moveTo(x, y);
                else ctx.lineTo(x, y);
            }
            ctx.stroke();

            // Fill under curve
            if (progress > 0) {
                ctx.beginPath();
                ctx.fillStyle = 'rgba(59, 130, 246, 0.1)';
                ctx.moveTo(margin.left, margin.top);
                for (let px = 0; px <= plotW * progress; px++) {
                    const t = (px / plotW) * maxT;
                    const n = Math.exp(-t);
                    ctx.lineTo(margin.left + px, margin.top + (1 - n) * plotH);
                }
                ctx.lineTo(margin.left + plotW * progress, margin.top + plotH);
                ctx.lineTo(margin.left, margin.top + plotH);
                ctx.closePath();
                ctx.fill();
            }

            // Half-life markers
            if (progress > 0.139) { // t=0.693 tau
                const hlX = margin.left + (0.693 / maxT) * plotW;
                const hlY = margin.top + 0.5 * plotH;
                ctx.setLineDash([4, 4]);
                ctx.strokeStyle = '#EF4444';
                ctx.lineWidth = 1;
                ctx.beginPath();
                ctx.moveTo(hlX, margin.top);
                ctx.lineTo(hlX, margin.top + plotH);
                ctx.stroke();
                ctx.beginPath();
                ctx.moveTo(margin.left, hlY);
                ctx.lineTo(margin.left + plotW, hlY);
                ctx.stroke();
                ctx.setLineDash([]);
                ctx.fillStyle = '#EF4444';
                ctx.font = 'bold 10px Inter, sans-serif';
                ctx.textAlign = 'left';
                ctx.fillText('t\u00BD = 0.693\u03C4', hlX + 4, margin.top + 12);
                ctx.fillText('N = N\u2080/2', margin.left + plotW - 55, hlY - 5);
            }

            // Current value dot
            if (progress > 0) {
                const dotT = tMax;
                const dotN = Math.exp(-dotT);
                const dotX = margin.left + (dotT / maxT) * plotW;
                const dotY = margin.top + (1 - dotN) * plotH;
                ctx.beginPath();
                ctx.arc(dotX, dotY, 5, 0, Math.PI * 2);
                ctx.fillStyle = '#3B82F6';
                ctx.fill();
                ctx.strokeStyle = 'white';
                ctx.lineWidth = 2;
                ctx.stroke();
            }

            frame++;
            if (frame > 200) frame = 0; // loop
            animId = requestAnimationFrame(draw);
        };

        draw();
        return () => cancelAnimationFrame(animId);
    }, []);

    return (
        <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
            <canvas ref={canvasRef} width={500} height={300} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};


// ============================================
// 6. Feynman Vertex  -  Ch 24
// Interactive QED vertex with labels
// ============================================
export const FeynmanVertex = () => {
    const canvasRef = useRef(null);

    useEffect(() => {
        const W = 520, H = 260;
        const ctx = setupCanvas(canvasRef.current, W, H);

        ctx.fillStyle = '#FAFAFA';
        ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B';
        ctx.font = 'bold 14px Inter, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('Feynman Diagrams: QED Vertex', W / 2, 22);

        const cx = 150, cy = 140;

        // --- Diagram 1: Basic QED vertex ---
        // Incoming electron
        ctx.strokeStyle = '#3B82F6';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(cx - 80, cy + 50);
        ctx.lineTo(cx, cy);
        ctx.stroke();
        // Arrow
        const drawArrow = (x1, y1, x2, y2, color) => {
            const mx = (x1 + x2) / 2, my = (y1 + y2) / 2;
            const angle = Math.atan2(y2 - y1, x2 - x1);
            ctx.fillStyle = color;
            ctx.beginPath();
            ctx.moveTo(mx + Math.cos(angle) * 6, my + Math.sin(angle) * 6);
            ctx.lineTo(mx + Math.cos(angle + 2.5) * 6, my + Math.sin(angle + 2.5) * 6);
            ctx.lineTo(mx + Math.cos(angle - 2.5) * 6, my + Math.sin(angle - 2.5) * 6);
            ctx.closePath();
            ctx.fill();
        };
        drawArrow(cx - 80, cy + 50, cx, cy, '#3B82F6');

        // Outgoing electron
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.lineTo(cx - 80, cy - 50);
        ctx.stroke();
        drawArrow(cx, cy, cx - 80, cy - 50, '#3B82F6');

        // Photon (wavy line)
        ctx.strokeStyle = '#EF4444';
        ctx.lineWidth = 2;
        ctx.beginPath();
        const photonEnd = cx + 90;
        for (let x = cx; x <= photonEnd; x += 2) {
            const progress = (x - cx) / (photonEnd - cx);
            const waveY = cy + Math.sin(progress * Math.PI * 6) * 8;
            if (x === cx) ctx.moveTo(x, waveY);
            else ctx.lineTo(x, waveY);
        }
        ctx.stroke();

        // Vertex dot
        ctx.beginPath();
        ctx.arc(cx, cy, 5, 0, Math.PI * 2);
        ctx.fillStyle = '#1E293B';
        ctx.fill();

        // Labels
        ctx.font = 'bold 12px Georgia, serif';
        ctx.textAlign = 'left';
        ctx.fillStyle = '#3B82F6';
        ctx.fillText('e\u207B', cx - 90, cy + 60);
        ctx.fillText("e\u207B'", cx - 90, cy - 50);
        ctx.fillStyle = '#EF4444';
        ctx.fillText('\u03B3 (photon)', photonEnd + 5, cy + 4);

        ctx.fillStyle = '#475569';
        ctx.font = '10px Inter, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('Basic QED Vertex', cx, cy + 85);
        ctx.fillText('Coupling: \u03B1 \u2248 1/137', cx, cy + 98);

        // --- Diagram 2: Electron-electron scattering ---
        const cx2 = W - 150, cy2 = 140;

        // Two electron lines
        ctx.strokeStyle = '#3B82F6';
        ctx.lineWidth = 2;
        // e1 in
        ctx.beginPath();
        ctx.moveTo(cx2 - 70, cy2 - 50);
        ctx.lineTo(cx2 - 20, cy2 - 20);
        ctx.stroke();
        drawArrow(cx2 - 70, cy2 - 50, cx2 - 20, cy2 - 20, '#3B82F6');
        // e1 out
        ctx.beginPath();
        ctx.moveTo(cx2 - 20, cy2 - 20);
        ctx.lineTo(cx2 + 50, cy2 - 50);
        ctx.stroke();
        drawArrow(cx2 - 20, cy2 - 20, cx2 + 50, cy2 - 50, '#3B82F6');
        // e2 in
        ctx.beginPath();
        ctx.moveTo(cx2 - 70, cy2 + 50);
        ctx.lineTo(cx2 - 20, cy2 + 20);
        ctx.stroke();
        drawArrow(cx2 - 70, cy2 + 50, cx2 - 20, cy2 + 20, '#3B82F6');
        // e2 out
        ctx.beginPath();
        ctx.moveTo(cx2 - 20, cy2 + 20);
        ctx.lineTo(cx2 + 50, cy2 + 50);
        ctx.stroke();
        drawArrow(cx2 - 20, cy2 + 20, cx2 + 50, cy2 + 50, '#3B82F6');

        // Virtual photon between vertices
        ctx.strokeStyle = '#EF4444';
        ctx.lineWidth = 2;
        ctx.beginPath();
        for (let y = cy2 - 20; y <= cy2 + 20; y += 2) {
            const progress = (y - (cy2 - 20)) / 40;
            const waveX = cx2 - 20 + Math.sin(progress * Math.PI * 4) * 6;
            if (y === cy2 - 20) ctx.moveTo(waveX, y);
            else ctx.lineTo(waveX, y);
        }
        ctx.stroke();

        // Vertex dots
        ctx.fillStyle = '#1E293B';
        ctx.beginPath(); ctx.arc(cx2 - 20, cy2 - 20, 4, 0, Math.PI * 2); ctx.fill();
        ctx.beginPath(); ctx.arc(cx2 - 20, cy2 + 20, 4, 0, Math.PI * 2); ctx.fill();

        // Labels
        ctx.font = 'bold 11px Georgia, serif';
        ctx.fillStyle = '#3B82F6';
        ctx.textAlign = 'right';
        ctx.fillText('e\u207B', cx2 - 75, cy2 - 52);
        ctx.fillText('e\u207B', cx2 - 75, cy2 + 48);
        ctx.textAlign = 'left';
        ctx.fillText('e\u207B', cx2 + 55, cy2 - 52);
        ctx.fillText('e\u207B', cx2 + 55, cy2 + 48);
        ctx.fillStyle = '#EF4444';
        ctx.textAlign = 'left';
        ctx.fillText('\u03B3*', cx2 - 10, cy2 + 4);

        ctx.fillStyle = '#475569';
        ctx.font = '10px Inter, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('M\u00F8ller Scattering (e\u207Be\u207B \u2192 e\u207Be\u207B)', cx2, cy2 + 85);
        ctx.fillText('Virtual photon exchange', cx2, cy2 + 98);

        // Bottom note
        ctx.fillStyle = '#64748B';
        ctx.font = '10px Inter, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('Time flows left \u2192 right | Fermions = solid lines | Bosons = wavy lines', W / 2, H - 10);

    }, []);

    return (
        <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-rose-50 rounded-2xl border border-slate-200">
            <canvas ref={canvasRef} width={520} height={260} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};


// ============================================
// 7. Beamline Schematic  -  Ch 27
// T9 beamline layout with labeled components
// ============================================
export const BeamlineSchematic = () => {
    const canvasRef = useRef(null);

    useEffect(() => {
        const W = 540, H = 175;
        const ctx = setupCanvas(canvasRef.current, W, H);
        let frame = 0;
        let animId;

        const draw = () => {
            ctx.clearRect(0, 0, W, H);
            ctx.fillStyle = '#FAFAFA';
            ctx.fillRect(0, 0, W, H);

            ctx.fillStyle = '#1E293B';
            ctx.font = 'bold 14px Inter, sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText('CERN T9 Beamline Schematic', W / 2, 22);

            const beamY = 95;
            const startX = 30, endX = W - 30;

            // Beam pipe
            ctx.fillStyle = '#F1F5F9';
            ctx.fillRect(startX, beamY - 8, endX - startX, 16);
            ctx.strokeStyle = '#94A3B8';
            ctx.lineWidth = 1;
            ctx.strokeRect(startX, beamY - 8, endX - startX, 16);

            // Components along the beamline
            const components = [
                { x: 55,  w: 30, h: 36, color: '#EF4444', label: 'Target', sublabel: 'Be/Pb' },
                { x: 115, w: 20, h: 30, color: '#8B5CF6', label: 'Dipole', sublabel: 'Bending' },
                { x: 160, w: 16, h: 26, color: '#3B82F6', label: 'Quad', sublabel: 'Focus' },
                { x: 200, w: 20, h: 30, color: '#8B5CF6', label: 'Dipole', sublabel: 'Select p' },
                { x: 250, w: 14, h: 22, color: '#22C55E', label: 'Collim.', sublabel: 'Shape' },
                { x: 300, w: 16, h: 26, color: '#3B82F6', label: 'Quad', sublabel: 'Focus' },
                { x: 345, w: 16, h: 22, color: '#F59E0B', label: 'BPM', sublabel: 'Monitor' },
                { x: 395, w: 20, h: 30, color: '#8B5CF6', label: 'Dipole', sublabel: 'Bend' },
                { x: 445, w: 16, h: 26, color: '#3B82F6', label: 'Quad', sublabel: 'Focus' },
                { x: 490, w: 40, h: 40, color: '#EC4899', label: 'Experiment', sublabel: 'BL4S' },
            ];

            components.forEach(comp => {
                const cy = beamY;
                ctx.fillStyle = comp.color + '30';
                ctx.beginPath();
                ctx.roundRect(comp.x - comp.w / 2, cy - comp.h / 2, comp.w, comp.h, 4);
                ctx.fill();
                ctx.strokeStyle = comp.color;
                ctx.lineWidth = 2;
                ctx.stroke();

                ctx.fillStyle = '#1E293B';
                ctx.font = 'bold 8px Inter, sans-serif';
                ctx.textAlign = 'center';
                ctx.fillText(comp.label, comp.x, beamY + comp.h / 2 + 14);
                ctx.fillStyle = '#94A3B8';
                ctx.font = '7px Inter, sans-serif';
                ctx.fillText(comp.sublabel, comp.x, beamY + comp.h / 2 + 24);
            });

            // PS source label
            ctx.fillStyle = '#EF4444';
            ctx.font = 'bold 10px Inter, sans-serif';
            ctx.textAlign = 'left';
            ctx.fillText('PS', startX + 2, beamY - 16);
            ctx.fillStyle = '#64748B';
            ctx.font = '8px Inter, sans-serif';
            ctx.fillText('24 GeV/c', startX + 2, beamY - 26);

            // Animated beam particles
            for (let i = 0; i < 6; i++) {
                const px = ((frame * 2 + i * 80) % (endX - startX)) + startX;
                const py = beamY + (Math.random() - 0.5) * 6;
                ctx.beginPath();
                ctx.arc(px, py, 2.5, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(239, 68, 68, ${0.8 - i * 0.1})`;
                ctx.fill();
            }

            // Legend at bottom
            ctx.fillStyle = '#475569';
            ctx.font = '10px Inter, sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText('Momentum range: 0.5 \u2013 10 GeV/c  |  Beam composition: \u03C0, K, p, e, \u03BC', W / 2, H - 14);

            // Color legend
            const legends = [
                { color: '#8B5CF6', text: 'Dipole' },
                { color: '#3B82F6', text: 'Quad' },
                { color: '#22C55E', text: 'Collimator' },
                { color: '#F59E0B', text: 'Monitor' },
            ];
            legends.forEach((l, i) => {
                const lx = 80 + i * 110;
                ctx.fillStyle = l.color;
                ctx.fillRect(lx, H - 32, 10, 10);
                ctx.fillStyle = '#64748B';
                ctx.font = '9px Inter, sans-serif';
                ctx.textAlign = 'left';
                ctx.fillText(l.text, lx + 14, H - 23);
            });

            frame++;
            animId = requestAnimationFrame(draw);
        };

        draw();
        return () => cancelAnimationFrame(animId);
    }, []);

    return (
        <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-violet-50 rounded-2xl border border-slate-200">
            <canvas ref={canvasRef} width={540} height={175} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};


// ============================================
// 8. Shower Development  -  Ch 23
// EM vs hadronic shower cascade visualization
// ============================================
export const ShowerDevelopment = () => {
    const canvasRef = useRef(null);

    useEffect(() => {
        const W = 520, H = 280;
        const ctx = setupCanvas(canvasRef.current, W, H);

        ctx.fillStyle = '#FAFAFA';
        ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B';
        ctx.font = 'bold 14px Inter, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('Particle Shower Development', W / 2, 22);

        // Draw tree-like shower
        const drawShower = (startX, startY, type, label) => {
            const isEM = type === 'em';
            const color1 = isEM ? '#3B82F6' : '#EF4444';
            const color2 = isEM ? '#60A5FA' : '#F87171';
            const depthMax = isEM ? 5 : 4;
            const spread = isEM ? 0.4 : 0.6;

            // Title
            ctx.fillStyle = color1;
            ctx.font = 'bold 11px Inter, sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText(label, startX, startY - 10);

            // Draw cascade branches recursively as a tree
            const branches = [];
            const generateBranches = (x, y, depth, angleBase) => {
                if (depth >= depthMax) return;
                const len = 25 - depth * 3;
                const leftAngle = angleBase - spread + (Math.random() - 0.5) * 0.2;
                const rightAngle = angleBase + spread + (Math.random() - 0.5) * 0.2;

                const x1 = x + Math.sin(leftAngle) * len;
                const y1 = y + Math.cos(0) * len;
                const x2 = x + Math.sin(rightAngle) * len;
                const y2 = y + Math.cos(0) * len;

                branches.push({ x1: x, y1: y, x2: x1, y2: y1, depth });
                branches.push({ x1: x, y1: y, x2: x2, y2: y2, depth });

                generateBranches(x1, y1, depth + 1, leftAngle);
                generateBranches(x2, y2, depth + 1, rightAngle);
            };

            generateBranches(startX, startY, 0, 0);

            branches.forEach(b => {
                ctx.strokeStyle = b.depth < 2 ? color1 : color2;
                ctx.lineWidth = Math.max(1, 3 - b.depth);
                ctx.globalAlpha = 1 - b.depth * 0.15;
                ctx.beginPath();
                ctx.moveTo(b.x1, b.y1);
                ctx.lineTo(b.x2, b.y2);
                ctx.stroke();

                // Particle dot at end
                ctx.beginPath();
                ctx.arc(b.x2, b.y2, Math.max(1, 3 - b.depth), 0, Math.PI * 2);
                ctx.fillStyle = b.depth < 2 ? color1 : color2;
                ctx.fill();
            });

            ctx.globalAlpha = 1;

            // Incoming particle
            ctx.strokeStyle = color1;
            ctx.lineWidth = 3;
            ctx.beginPath();
            ctx.moveTo(startX, startY - 30);
            ctx.lineTo(startX, startY);
            ctx.stroke();

            // Arrow head
            ctx.fillStyle = color1;
            ctx.beginPath();
            ctx.moveTo(startX, startY);
            ctx.lineTo(startX - 4, startY - 8);
            ctx.lineTo(startX + 4, startY - 8);
            ctx.closePath();
            ctx.fill();
        };

        // Seed the random generator for consistent display
        // (We use a simple approach: draw once)
        drawShower(W / 4, 65, 'em', 'EM Shower (e\u207B or \u03B3)');
        drawShower(3 * W / 4, 65, 'hadronic', 'Hadronic Shower (\u03C0, p)');

        // Comparison table at bottom
        ctx.fillStyle = '#475569';
        ctx.font = '10px Inter, sans-serif';
        ctx.textAlign = 'center';
        const tableY = H - 45;
        ctx.fillText('EM: narrow, short (X\u2080 scale)', W / 4, tableY);
        ctx.fillText('Hadronic: wider, deeper (\u03BB scale)', 3 * W / 4, tableY);

        ctx.fillStyle = '#64748B';
        ctx.font = '9px Inter, sans-serif';
        ctx.fillText('EM: e\u207B\u2192\u03B3\u2192e\u207Be\u207A (pair production + bremsstrahlung)', W / 2, H - 20);
        ctx.fillText('Hadronic: \u03C0\u2192\u03C0\u03C0\u03C0, nuclear spallation, \u03C0\u2070\u2192\u03B3\u03B3', W / 2, H - 8);

    }, []);

    return (
        <div className="my-8 p-6 bg-gradient-to-br from-blue-50 to-red-50 rounded-2xl border border-slate-200">
            <canvas ref={canvasRef} width={520} height={280} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};


// ============================================
// 9. Bragg Curve Chart  -  Ch 8/medical
// Dose vs depth with Bragg peak
// ============================================
export const BraggCurveChart = () => {
    const canvasRef = useRef(null);

    useEffect(() => {
        const W = 500, H = 280;
        const ctx = setupCanvas(canvasRef.current, W, H);

        ctx.fillStyle = '#FAFAFA';
        ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B';
        ctx.font = 'bold 14px Inter, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('Bragg Curve: Dose vs Depth', W / 2, 22);

        const margin = { top: 42, right: 30, bottom: 40, left: 50 };
        const plotW = W - margin.left - margin.right;
        const plotH = H - margin.top - margin.bottom;

        // Axes
        ctx.strokeStyle = '#1E293B';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(margin.left, margin.top);
        ctx.lineTo(margin.left, margin.top + plotH);
        ctx.lineTo(margin.left + plotW, margin.top + plotH);
        ctx.stroke();

        // Y label
        ctx.save();
        ctx.translate(16, margin.top + plotH / 2);
        ctx.rotate(-Math.PI / 2);
        ctx.fillStyle = '#475569';
        ctx.font = '11px Inter, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('Relative Dose', 0, 0);
        ctx.restore();

        // X label
        ctx.fillStyle = '#475569';
        ctx.font = '11px Inter, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('Depth in tissue (cm)', margin.left + plotW / 2, H - 6);

        // Bragg curve for protons (characteristic shape)
        const braggProton = (x) => {
            // Normalized x from 0 to 1 (0=surface, 1=max range)
            if (x > 1.02) return 0;
            const plateau = 0.3;
            const peak = 1.0;
            if (x < 0.85) return plateau + x * 0.15;
            // Sharp peak near end
            const t = (x - 0.85) / 0.15;
            if (t < 0.7) return plateau + 0.15 * 0.85 + t / 0.7 * (peak - plateau - 0.15 * 0.85);
            // Fall off
            const fallT = (t - 0.7) / 0.3;
            return peak * (1 - fallT * fallT);
        };

        // X-ray/photon curve (exponential falloff)
        const photonCurve = (x) => {
            return 0.85 * Math.exp(-1.5 * x) + 0.1;
        };

        // Draw photon curve
        ctx.beginPath();
        ctx.strokeStyle = '#94A3B8';
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 3]);
        for (let px = 0; px <= plotW; px++) {
            const x = px / plotW;
            const val = photonCurve(x);
            const plotX = margin.left + px;
            const plotY = margin.top + (1 - val) * plotH;
            if (px === 0) ctx.moveTo(plotX, plotY);
            else ctx.lineTo(plotX, plotY);
        }
        ctx.stroke();
        ctx.setLineDash([]);

        // Draw proton Bragg curve
        ctx.beginPath();
        ctx.strokeStyle = '#3B82F6';
        ctx.lineWidth = 3;
        for (let px = 0; px <= plotW; px++) {
            const x = px / plotW;
            const val = braggProton(x);
            const plotX = margin.left + px;
            const plotY = margin.top + (1 - val) * plotH;
            if (px === 0) ctx.moveTo(plotX, plotY);
            else ctx.lineTo(plotX, plotY);
        }
        ctx.stroke();

        // Fill under Bragg curve
        ctx.beginPath();
        ctx.fillStyle = 'rgba(59, 130, 246, 0.08)';
        ctx.moveTo(margin.left, margin.top + plotH);
        for (let px = 0; px <= plotW; px++) {
            const x = px / plotW;
            const val = braggProton(x);
            ctx.lineTo(margin.left + px, margin.top + (1 - val) * plotH);
        }
        ctx.lineTo(margin.left + plotW, margin.top + plotH);
        ctx.closePath();
        ctx.fill();

        // Bragg peak label
        const peakX = margin.left + 0.93 * plotW;
        const peakY = margin.top + (1 - 1.0) * plotH;
        ctx.fillStyle = '#3B82F6';
        ctx.font = 'bold 11px Inter, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('Bragg Peak', peakX - 35, peakY - 8);

        // Tumor region
        ctx.fillStyle = 'rgba(239, 68, 68, 0.1)';
        const tumorX = margin.left + 0.8 * plotW;
        const tumorW = 0.15 * plotW;
        ctx.fillRect(tumorX, margin.top, tumorW, plotH);
        ctx.strokeStyle = '#EF4444';
        ctx.lineWidth = 1;
        ctx.setLineDash([3, 3]);
        ctx.strokeRect(tumorX, margin.top, tumorW, plotH);
        ctx.setLineDash([]);
        ctx.fillStyle = '#EF4444';
        ctx.font = 'bold 10px Inter, sans-serif';
        ctx.fillText('Tumor', tumorX + tumorW / 2, margin.top + 14);

        // Legend
        ctx.font = '10px Inter, sans-serif';
        ctx.textAlign = 'left';
        ctx.strokeStyle = '#3B82F6';
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(margin.left + 10, margin.top + 10);
        ctx.lineTo(margin.left + 30, margin.top + 10);
        ctx.stroke();
        ctx.fillStyle = '#3B82F6';
        ctx.fillText('Protons', margin.left + 35, margin.top + 14);

        ctx.strokeStyle = '#94A3B8';
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 3]);
        ctx.beginPath();
        ctx.moveTo(margin.left + 10, margin.top + 26);
        ctx.lineTo(margin.left + 30, margin.top + 26);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = '#94A3B8';
        ctx.fillText('X-rays', margin.left + 35, margin.top + 30);

    }, []);

    return (
        <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-cyan-50 rounded-2xl border border-slate-200">
            <canvas ref={canvasRef} width={500} height={280} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
            <p className="text-center text-sm text-slate-500 mt-3">Protons deposit maximum dose at the Bragg peak, sparing healthy tissue</p>
        </div>
    );
};


// ============================================
// 10. Lorentz Boost Diagram  -  Ch 11
// Minkowski diagram with light cones
// ============================================
export const LorentzBoostDiagram = () => {
    const canvasRef = useRef(null);

    useEffect(() => {
        const W = 480, H = 300;
        const ctx = setupCanvas(canvasRef.current, W, H);

        ctx.fillStyle = '#FAFAFA';
        ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B';
        ctx.font = 'bold 14px Inter, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('Minkowski Spacetime Diagram', W / 2, 22);

        const cx = W / 2, cy = H / 2 + 10;
        const axisLen = 110;

        // Light cone (45 degree lines)
        ctx.strokeStyle = '#FCD34D';
        ctx.lineWidth = 2;
        ctx.setLineDash([6, 3]);
        // Future light cone
        ctx.beginPath();
        ctx.moveTo(cx - axisLen, cy - axisLen);
        ctx.lineTo(cx, cy);
        ctx.lineTo(cx + axisLen, cy - axisLen);
        ctx.stroke();
        // Past light cone
        ctx.beginPath();
        ctx.moveTo(cx - axisLen, cy + axisLen);
        ctx.lineTo(cx, cy);
        ctx.lineTo(cx + axisLen, cy + axisLen);
        ctx.stroke();
        ctx.setLineDash([]);

        // Light cone fill
        ctx.fillStyle = 'rgba(252, 211, 77, 0.08)';
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.lineTo(cx - axisLen, cy - axisLen);
        ctx.lineTo(cx + axisLen, cy - axisLen);
        ctx.closePath();
        ctx.fill();
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.lineTo(cx - axisLen, cy + axisLen);
        ctx.lineTo(cx + axisLen, cy + axisLen);
        ctx.closePath();
        ctx.fill();

        // ct axis (vertical)
        ctx.strokeStyle = '#1E293B';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(cx, cy + axisLen + 10);
        ctx.lineTo(cx, cy - axisLen - 10);
        ctx.stroke();
        // Arrow
        ctx.fillStyle = '#1E293B';
        ctx.beginPath();
        ctx.moveTo(cx, cy - axisLen - 15);
        ctx.lineTo(cx - 5, cy - axisLen - 5);
        ctx.lineTo(cx + 5, cy - axisLen - 5);
        ctx.closePath();
        ctx.fill();
        ctx.font = 'bold 12px Inter, sans-serif';
        ctx.textAlign = 'right';
        ctx.fillText('ct', cx - 8, cy - axisLen - 5);

        // x axis (horizontal)
        ctx.strokeStyle = '#1E293B';
        ctx.beginPath();
        ctx.moveTo(cx - axisLen - 10, cy);
        ctx.lineTo(cx + axisLen + 10, cy);
        ctx.stroke();
        // Arrow
        ctx.beginPath();
        ctx.moveTo(cx + axisLen + 15, cy);
        ctx.lineTo(cx + axisLen + 5, cy - 5);
        ctx.lineTo(cx + axisLen + 5, cy + 5);
        ctx.closePath();
        ctx.fill();
        ctx.textAlign = 'left';
        ctx.fillText('x', cx + axisLen + 8, cy - 8);

        // Boosted frame axes (v = 0.5c, beta = 0.5)
        const beta = 0.5;
        const boostAngle = Math.atan(beta); // angle of ct' axis from ct

        // ct' axis (tilted toward x)
        ctx.strokeStyle = '#3B82F6';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(cx - Math.sin(boostAngle) * (axisLen + 10), cy + Math.cos(boostAngle) * (axisLen + 10));
        ctx.lineTo(cx + Math.sin(boostAngle) * (axisLen + 10), cy - Math.cos(boostAngle) * (axisLen + 10));
        ctx.stroke();

        // x' axis (tilted toward ct)
        ctx.beginPath();
        ctx.moveTo(cx - Math.cos(boostAngle) * (axisLen + 10), cy + Math.sin(boostAngle) * (axisLen + 10));
        ctx.lineTo(cx + Math.cos(boostAngle) * (axisLen + 10), cy - Math.sin(boostAngle) * (axisLen + 10));
        ctx.stroke();

        // Labels
        ctx.fillStyle = '#3B82F6';
        ctx.font = 'bold 11px Inter, sans-serif';
        ctx.textAlign = 'left';
        ctx.fillText("ct'", cx + Math.sin(boostAngle) * (axisLen + 5) + 5, cy - Math.cos(boostAngle) * (axisLen + 5));
        ctx.fillText("x'", cx + Math.cos(boostAngle) * (axisLen + 5) + 5, cy - Math.sin(boostAngle) * (axisLen + 5));

        // Region labels
        ctx.fillStyle = '#F59E0B';
        ctx.font = 'bold 11px Inter, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('FUTURE', cx, cy - axisLen + 12);
        ctx.fillText('PAST', cx, cy + axisLen - 5);

        ctx.fillStyle = '#94A3B8';
        ctx.font = '10px Inter, sans-serif';
        ctx.fillText('SPACELIKE', cx - axisLen + 25, cy + 5);
        ctx.fillText('SPACELIKE', cx + axisLen - 25, cy + 5);

        // Light cone label
        ctx.fillStyle = '#F59E0B';
        ctx.font = 'italic 10px Inter, sans-serif';
        ctx.textAlign = 'left';
        ctx.fillText('v = c', cx + axisLen - 20, cy - axisLen + 5);

        // Origin
        ctx.beginPath();
        ctx.arc(cx, cy, 4, 0, Math.PI * 2);
        ctx.fillStyle = '#1E293B';
        ctx.fill();

        // Bottom legend
        ctx.fillStyle = '#475569';
        ctx.font = '10px Inter, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText("Black: rest frame (x, ct)  |  Blue: boosted frame (x', ct')  |  \u03B2 = 0.5", W / 2, H - 10);

    }, []);

    return (
        <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-amber-50 rounded-2xl border border-slate-200">
            <canvas ref={canvasRef} width={480} height={300} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};


export default {
    EMSpectrum,
    TimeDilationDiagram,
    AcceleratorTypes,
    DetectorCrossSection,
    DecayCurve,
    FeynmanVertex,
    BeamlineSchematic,
    ShowerDevelopment,
    BraggCurveChart,
    LorentzBoostDiagram,
};
